// src/components/Header.js

import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { trackEvent } from '../services/analytics.js';
import { useRealtime } from '../contexts/RealtimeContext.js';
import useRemix from '../hooks/useRemix.js'
import usePublish from '../hooks/usePublish.js';
import {
  IconButton,
  Menu,
  MenuItem,
  Modal,
  Box,
  Typography,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Snackbar } from '@mui/material';
import MenuIcon        from '@mui/icons-material/Menu';
import VisibilityIcon  from '@mui/icons-material/Visibility';
import CodeIcon        from '@mui/icons-material/Code';
import ModeCommentIcon from '@mui/icons-material/ModeComment';
import SettingsIcon    from '@mui/icons-material/Settings';
import AddBoxIcon      from '@mui/icons-material/AddBox';
import GridViewIcon    from '@mui/icons-material/GridView';
import ConnectWithoutContactIcon from '@mui/icons-material/ConnectWithoutContact'
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CallSplitIcon from '@mui/icons-material/CallSplit'
import IosShare    from '@mui/icons-material/IosShare';
import PublishIcon     from '@mui/icons-material/Publish';

import ProductHuntBanner from './ProductHuntBanner.js';

const modalBoxStyle = {
  position:      'absolute',
  top:           '50%',
  left:          '50%',
  transform:     'translate(-50%, -50%)',
  bgcolor:       'white',
  border:        '1px solid #ccc',
  borderRadius:  '8px',
  boxShadow:     24,
  p:             4,
  width:         '90%',
  maxWidth:      '700px',
};

export default function Header({
  viewMode,
  toggleView,
  sidebarVisible,
  setSidebarVisible,
  history,
  timestamps,
  historyIndex,
  jumpToVersion,
  onSettingsClick,
  isOwner,
  isController,
  takeControl,
  collabSessionActive,
  setCollabSessionActive,
  collabSessionElapsed,
  optionState,
  jumpToOption
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const socket = useRealtime(); 
  const { username, chatId: prototypeId } = useParams();
  const navigate     = useNavigate();
  const [anchorEl,   setAnchorEl]   = useState(null);
  const [openModal,  setOpenModal]  = useState(null);
  const location     = useLocation();
  const isPrototypePage = location.pathname.split('/').filter(Boolean).length >= 2;
  const [showSessionDialog, setShowSessionDialog] = useState(false);
  const canonicalPrototypeRoute = `/${username}/${prototypeId}`;
  const collabSessionRoute = `/s/${username}/${prototypeId}`
  const collabSessionLink = `${window.location.origin}${collabSessionRoute}`;
  const sessionRoom = `${username}/${prototypeId}`;

  // Is the mobile user still deciding which Option to accept?
  const hasPendingOptions = optionState && optionState.status === 'pending';
  const hideMobileOptionUI = isMobile && hasPendingOptions;

  // --- Share menu state ---
  const [shareAnchorEl, setShareAnchorEl] = useState(null);
  const openShare = e => {
    trackEvent({
      action:   'click_share',
      category: 'navigation',
      label:    'Share/Actions'
    });
    setShareAnchorEl(e.currentTarget);
    };
  const closeShare = () => setShareAnchorEl(null);
  const currenVersionLabel = history.length
    ? `Version ${historyIndex+1} — ${timestamps[historyIndex]}`
    : '';

  // publish hook
  const { publish, loading: publishing } = usePublish(prototypeId);
  const [copySnackbar, setCopySnackbar] = useState(false);
  const [pubSnackbar,  setPubSnackbar]  = useState(false);

  // detect production
  const isProd = process.env.NODE_ENV === 'production';

  const prototypesPath = username ? `/${username}/prototypes` : '/prototypes';

  const handleMenuOpen = e => {
  setAnchorEl(e.currentTarget);
  trackEvent({
    action:   'open_menu',
    category: 'navigation',
    label:    'hamburger'
  });
};
  const handleMenuClose = () => setAnchorEl(null);
  const openDialog      = type => { setOpenModal(type); handleMenuClose(); };
  const closeDialog     = ()   => setOpenModal(null);

  // helper to reset state by reloading
  const goHomeAndReload = () => {
    navigate(`/${username}`);
    window.location.reload();
  };

  // Remix hook + state
  const { remix, loading } = useRemix()
  const [isRemixOpen, setIsRemixOpen]     = useState(false)
  const [remixError, setRemixError]       = useState(null)


const handleRemixClick = () => {
  trackEvent({ 
        action:   'click_remix',
    category: 'navigation',
    label:    'Remix Prototype'
  });
  setIsRemixOpen(true);
};
  const handleRemixClose = () => setIsRemixOpen(false)

  const handleRemixConfirm = async () => {
    setRemixError(null)
    try {
      const newId = await remix(prototypeId, historyIndex)
      // Navigate with state to trigger snackbar in new pag
      navigate(
        `/${username}/${newId}`,
        {
          // existing flags …
          state: {
            showCongrats:   true,
            sidebarVisible: true,
            /* ↓ lets the next page know we just forked */
            justRemixed:    true,
          },
        }
      )
      setIsRemixOpen(false)
    } catch (e) {
      setRemixError(e.message)
    }
  }

  // Snackbar state
  const [showSnackbar, setShowSnackbar] = useState(false)

  // Show snackbar on new page load if remix just happened
  useEffect(() => {
    if (location.state?.showCongrats) {
      setShowSnackbar(true)
      setSidebarVisible(true)
      // Clean up location state so it doesn't re-trigger
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location, navigate, setSidebarVisible])

  // auto‐open the "share link" dialog if navigate(..., {state:{showSessionDialog:true}})
  useEffect(() => {
    if (location.state?.showSessionDialog) {
      setShowSessionDialog(true);
      // clear it so it doesn't reopen on every render
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  const myId = socket.id;
  useEffect(() => {
    // only the owner (the one who started the session) should auto‐claim host
    if (!collabSessionActive || !isOwner) return;

    console.log('[Header] owner entering collab mode → join + claim host');
    // 1) join the room
    socket.emit('join', { room: sessionRoom });

    // 2) once joined, claim controller
    socket.emit('set_controller', {
      room:         sessionRoom,
      controllerId: myId,
    });
  }, [ collabSessionActive, isOwner, sessionRoom, socket, myId ]);

  return (
    <>
      {/* Top bar: brand + action icons */}
      <Box
        sx={{
          p: 1.5,
          fontSize: '20px',
          fontWeight: 'bold',
          background: '#fff',
          borderBottom: '1px solid #ddd',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {/* Left: menu + brand */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={handleMenuOpen} size="small" sx={{ mr: 1, color: 'black' }}>
            <MenuIcon />
          </IconButton>
           <Typography
             variant="h6"
             onClick={() => {
               trackEvent({
                 action:   'click_logo',
                 category: 'navigation',
                 label:    'OpenJam Logo > Home',
               });
               goHomeAndReload();
             }}
             sx={{ fontWeight: 600, cursor: 'pointer' }}
           >
             OpenJam
           </Typography>
        </Box>

        {/* Right: version, view toggles, chat & settings */}
        {isPrototypePage && ['canvas', 'code', 'comment'].includes(viewMode) && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {/* -- hide the whole dropdown block while the mobile picker is active -- */}
            {!hideMobileOptionUI && (
              optionState && optionState.status === 'pending' ? (
                /* ▸ PENDING-OPTIONS dropdown */
                <select
                  className="history-select"
                  value={optionState.hoveredId ?? optionState.options[0].id}
                  disabled={
                    (!isController && collabSessionActive) ||
                    !optionState.options.length
                  }
                  onChange={e => {
                    const idx = optionState.options.findIndex(
                      o => o.id === +e.target.value
                    );
                    if (idx >= 0) jumpToOption(idx);
                  }}
                  style={{ marginRight: 16 }}
                >
                  {optionState.options.map((o, i) => (
                    <option key={o.id} value={o.id}>
                      Option {String.fromCharCode(65 + i)}
                      {o.id === optionState.acceptedId ? ' (accepted)' : ''}
                    </option>
                  ))}
                </select>
              ) : (
                /* ▸ NORMAL “Versions” dropdown */
                <select
                  className="history-select"
                  disabled={
                    !history.length || (!isController && collabSessionActive)
                  }
                  value={historyIndex}
                  onChange={e => {
                    const idx = +e.target.value;
                    jumpToVersion(idx);
                    navigate(`/${username}/${prototypeId}/${idx}`, {
                      replace: true
                    });
                    if (isController) {
                      socket.emit('version_change', {
                        room: prototypeId,
                        versionIndex: idx,
                        clientId: socket.id
                      });
                    }
                  }}
                  style={{ marginRight: 16 }}
                >
                  {!history.length ? (
                    <option>No Versions Yet</option>
                  ) : (
                    history.map((_, i) => (
                      <option key={i} value={i}>
                        Version {i + 1} — {timestamps[i]}
                        {i === historyIndex ? ' (active)' : ''}
                      </option>
                    ))
                  )}
                </select>
              )
            )}
            {/* ────────────────────────────────────────────── */}

            {/* Hide Remix + Share block while the mobile picker is showing */}
            {!hideMobileOptionUI && (
              <>
                {/* ── Remix button ───────────────────────────────────────────── */}
                <IconButton onClick={handleRemixClick} title="Remix this prototype">
                  <CallSplitIcon />
                </IconButton>

                <Dialog open={isRemixOpen} onClose={handleRemixClose}>
                  <DialogTitle>Remix Prototype</DialogTitle>
                  <DialogContent>
                    <Typography sx={{ mb: 2 }}>
                      This will create a new, editable copy in your account (starting at the
                      current selected version). Your changes won't affect the original.
                    </Typography>
                    {remixError && <Typography color="error">{remixError}</Typography>}
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={handleRemixClose} disabled={loading}>Cancel</Button>
                    <Button onClick={handleRemixConfirm} disabled={loading} variant="contained">
                      {loading ? 'Remixing…' : 'Create & Open'}
                    </Button>
                  </DialogActions>
                </Dialog>

                {/* success toast */}
                <Snackbar
                  open={showSnackbar}
                  autoHideDuration={3000}
                  onClose={() => setShowSnackbar(false)}
                  anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                  message="Congrats! You created a Remix!"
                  ContentProps={{
                    sx: {
                      mb: '72px',
                      animation: 'bounce 1s',
                      fontWeight: 'bold',
                      backgroundColor: 'primary.main',
                    }
                  }}
                />

                {/* ── Share button / menu ────────────────────────────────────── */}
                <IconButton
                  onClick={openShare}
                  title="Share / actions"
                  sx={{ mx: 0.5, fontSize: 24 }}
                >
                  <IosShare />
                </IconButton>

                <Menu
                  anchorEl={shareAnchorEl}
                  open={Boolean(shareAnchorEl)}
                  onClose={closeShare}
                >
                {/* 1) Copy link to current version */}
                <MenuItem
                  onClick={() => {
                    const url = `${window.location.origin}/${username}/${prototypeId}/${historyIndex}`;
                    navigator.clipboard.writeText(url);
                    setCopySnackbar(true);
                    closeShare();
                  }}
                >
                  <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary={`Copy link to ${currenVersionLabel}`} />
                </MenuItem>

                {/* 2) Start session */}
                {isOwner && !collabSessionActive && (
                  <MenuItem
                    onClick={() => {
                      setCollabSessionActive(true);
                      navigate(collabSessionRoute, { state: { showSessionDialog: true }});
                      closeShare();
                    }}
                  >
                    <ListItemIcon><ConnectWithoutContactIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Start collaboration session" />
                  </MenuItem>
                )}

                {/* 3) Publish version */}
                {isOwner && (
                  <MenuItem
                    disabled={publishing}
                    onClick={async () => {
                      await publish(historyIndex);
                      setPubSnackbar(true);
                      closeShare();
                    }}
                  >
                    <ListItemIcon><PublishIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary={`Publish ${currenVersionLabel}`} />
                  </MenuItem>
                )}
              </Menu>
             </>
            )}

            {viewMode === 'canvas' ? (
              <CodeIcon
                title="Show code"
                sx={{ cursor: 'pointer', mx: 0.5, fontSize: 24, color: 'black' }}
                onClick={() => {
                  trackEvent({
                    action:   'click_code_view',
                    category: 'navigation',
                    label:    'Code View'
                  });
                  if (isMobile && sidebarVisible) {
                    window.dispatchEvent(new Event('closeMobileSidebar'));
                  }
                  toggleView('code');
                }}
              />
             ) : (
               <VisibilityIcon
                 title="Show canvas"
                 sx={{ cursor: 'pointer', mx: 0.5, fontSize: 24, color: 'black' }}
                 onClick={() => {
                   trackEvent({
                     action:   'click_canvas_view',
                     category: 'navigation',
                     label:    'Canvas View'
                   });
                   toggleView('canvas');
                 }}
               />
             )}
            

            {/* Only show chat icon if not mobile */}
            {!isMobile && (
              <IconButton
              onClick={() => {
                trackEvent({
                  action:   'click_toggle_chat',
                  category: 'navigation',
                  label:    sidebarVisible ? 'Hide Chat' : 'Show Chat'
                });
                setSidebarVisible(v => !v);
              }}
                title={isOwner ? 'Toggle chat' : 'View chat (read-only)'}
                sx={{ mx: 1 }}
              >
                <ModeCommentIcon
                  sx={{
                    fontSize: 24,
                    color: sidebarVisible ? 'black' : '#888'
                  }}
                />
              </IconButton>
            )}

            {!isProd && (
              <IconButton onClick={onSettingsClick} sx={{ mx: 1 }}>
                <SettingsIcon sx={{ fontSize: 24, color: '#888' }} />
              </IconButton>
            )}
          </Box>
        )}
      </Box>

      {/* ─────────────── Product Hunt banner (a separate component) ─────────────── */}
      {!isPrototypePage && <ProductHuntBanner />}

      {/* Bottom bar: control button only */}
      {collabSessionActive && isPrototypePage && ['canvas','code','comment'].includes(viewMode) && (
        <Box
          sx={{
            p: 1,
            background: '#fff',
            borderBottom: '1px solid #ddd',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {isController ? (
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              You are the Host
            </Typography>
          ) : (
            <Button variant="outlined" color="primary" onClick={takeControl}>
              Become Host
            </Button>
          )}

          {/* Stop Session */}
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              if (isController) {
                // end for everyone
                socket.emit('stop_session', { room: sessionRoom });
              }
              setCollabSessionActive(false);
              navigate(canonicalPrototypeRoute);
            }}
            sx={{ ml: 2 }}
          >
            {isController ? 'Stop Session' : 'Leave Session'}
          </Button>

          {/* Session Timer to the right of Stop Session */}
          <Typography
            variant="body2"
            sx={{ ml: 2, fontFamily: 'monospace' }}
          >
            {collabSessionElapsed}
          </Typography>
        </Box>
      )}

      {/* sidebar menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{ sx:{ width:250, maxWidth:'100%' } }}
      >
        <Box sx={{ height:5 }} />
        <MenuItem
          component={Link}
          to={prototypesPath}
          onClick={() => {
            trackEvent({
              action:   'select_menu_item',
              category: 'navigation',
              label:    'My Gallery'
            });
            handleMenuClose();
          }}
        >
          <GridViewIcon sx={{ mr:1, color:'#1226eb' }} />
          My Gallery
        </MenuItem>
        <MenuItem
          onClick={() => {
            trackEvent({
              action:   'select_menu_item',
              category: 'navigation',
              label:    'Create New'
            });
            handleMenuClose();
            goHomeAndReload();
          }}
        >
          <AddBoxIcon sx={{ mr:1, color:'#1226eb' }} />
          Create New
        </MenuItem>
        <Box sx={{ height:60 }} />
        <Divider />
        <Box sx={{ height:8 }} />
        <MenuItem
          onClick={() => {
            trackEvent({
              action:   'select_menu_item',
              category: 'navigation',
              label:    'What is OpenJam?'
            });
            openDialog('about');
          }}
          sx={{ fontSize: '0.875rem' }}
        >
          What is OpenJam?
        </MenuItem>
        {/* <MenuItem onClick={() => openDialog('pricing')}  sx={{ fontSize:'0.875rem' }}>
          Pricing
        </MenuItem> */}
      </Menu>

      {/* about modal */}
      <Modal open={openModal==='about'} onClose={closeDialog}>
        <Box sx={modalBoxStyle}>
          <Typography variant="h5">Jam on Your Ideas</Typography>
          <Typography sx={{ mt:2 }}>
            OpenJam is a fun prototyping tool for everyone /
            Just chat and watch your canvas come to life
          </Typography>
        </Box>
      </Modal>

      {/* pricing modal */}
      <Modal open={openModal==='pricing'} onClose={closeDialog}>
        <Box sx={modalBoxStyle}>
          <Typography variant="h5">Pricing</Typography>
          <Typography sx={{ mt:2 }}>
            Currently free while in beta. Future pricing will be based on usage
            tiers with generous free limits.
          </Typography>
        </Box>
      </Modal>

      {showSessionDialog && (
        <Dialog open={showSessionDialog} onClose={() => setShowSessionDialog(false)}>
          <DialogTitle>Invite others to your Collaboration Session</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Share this link to invite others to your session
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <input
                type="text"
                readOnly
                value={collabSessionLink}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: '14px',
                }}
              />
              <IconButton
                onClick={() => navigator.clipboard.writeText(collabSessionLink)}
                sx={{ ml: 1 }}
                size="medium"
                title="Copy link"
              >
                <ContentCopyIcon fontSize="medium" />
              </IconButton>
            </Box>

          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowSessionDialog(false)}>Close</Button>
            <Button
              onClick={() => {
                navigator.clipboard.writeText(collabSessionLink)
              }}
              variant="contained"
            >
              Copy Link
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {/* Copy link confirmation */}
      <Snackbar
        open={copySnackbar}
        autoHideDuration={2000}
        onClose={() => setCopySnackbar(false)}
        message="Link copied to clipboard"
      />

      {/* Publish confirmation */}
      <Snackbar
        open={pubSnackbar}
        autoHideDuration={2000}
        onClose={() => setPubSnackbar(false)}
        message={`Version ${historyIndex+1} published!`}
      />
    </>
  );
}
